/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  registrationDateTimeToUtcIso,
  registrationDateTimeFromUtcIso,
} from '@sportninja/common/ts-utils/registration-datetime'
import { formatTimezoneLabel } from '@sportninja/common/ts-utils/timezone-display'
import Text from 'src/components/ts-components/Text'
import RegistrationWindowFields, {
  RegistrationWindowChange,
} from './RegistrationWindowFields'
import { useRegistrationSetupContext } from '../../context'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
// import { SelectInput } from 'src/components/ts-components/SelectInput'
import MultipleOptionsInput from 'src/components/ts-components/MultipleOptionsInput'
import colors from '@sportninja/common/constants/appColors'
import CopyComponent from 'src/components/CopyComponent'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const GeneralSettings = () => {
  const { state, setState, registrationId, apiAccount } =
    useRegistrationSetupContext()

  const accountTimezone = apiAccount?.address?.timezone

  // Shows 'PDT' / 'EST' rather than the raw IANA name, and marks the case where
  // the account has no timezone and the viewer's own is being used instead.
  const timezoneLabel = `Registration Start/End (${formatTimezoneLabel(
    accountTimezone
  )})`

  // The window is stored as UTC ISO strings but edited in the account's
  // timezone, so convert on the way in and back out again.
  const startsAt = registrationDateTimeFromUtcIso(
    state?.competitionDateRange?.starts_at,
    accountTimezone
  )
  const endsAt = registrationDateTimeFromUtcIso(
    state?.competitionDateRange?.ends_at,
    accountTimezone
  )

  const setCompetitionWindow = ({
    startsAt: nextStart,
    endsAt: nextEnd,
  }: RegistrationWindowChange) => {
    const nextStartsAt = registrationDateTimeToUtcIso(
      nextStart,
      accountTimezone
    )
    const nextEndsAt = registrationDateTimeToUtcIso(nextEnd, accountTimezone)
    const bothFilled = Boolean(nextStartsAt && nextEndsAt)
    const nextCustomError = { ...(state.customError || {}) }
    if (bothFilled) {
      delete nextCustomError.competitionDateRange
    }
    setState({
      ...state,
      competitionDateRange: {
        starts_at: nextStartsAt,
        ends_at: nextEndsAt,
      },
      fieldErrors: bothFilled
        ? state.fieldErrors.filter((f) => f !== 'competitionDateRange')
        : state.fieldErrors,
      customError: nextCustomError,
    })
  }

  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
          justify-content: space-between;
          width: 100%;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          General Settings
        </Text>
        {registrationId ? (
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <CopyComponent
              id={registrationId}
              customText='Registration ID'
              alternativeBackground={true}
            />
          </div>
        ) : null}
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          width: 50%;
          min-width: 380px;
        `}
      >
        <RegistrationWindowFields
          label={timezoneLabel}
          startsAt={startsAt}
          endsAt={endsAt}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          onChange={setCompetitionWindow}
        />
      </div>
      <div
        css={css`
          width: 100%;
          height: 1px;
          background: ${colors.NEUTRAL_100};
          border-bottom: 1px solid ${colors.NEUTRAL_100};
        `}
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionSmall' weight='semiBold'>
          Registration Type
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Enable Team Registration'
          subtitle='Allow Team Officials To Register To Their Team'
          value={state.enableTeamRegistration}
          onChange={(value) => {
            setState({ ...state, enableTeamRegistration: value })
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Enable Player Registration'
          subtitle='Allow Players to Register to Teams and/or Drafts'
          value={state.enablePlayerRegistration}
          onChange={(value) => {
            setState({
              ...state,
              enablePlayerRegistration: value,
            })
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Add Players Outside Of Registration'
          subtitle='Allow Team Officials To Be Able To Add Players Outside Of Registration'
          value={state.addPlayersOutsideOfRegistration}
          onChange={(value) => {
            setState({ ...state, addPlayersOutsideOfRegistration: value })
          }}
        />
      </div>
      {/* Removing this for now in favor of the switch input */}
      {/* <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <SelectInput
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            label='Registration Type'
            options={[
              { label: 'Team', value: 'team' },
              { label: 'Player', value: 'player' },
            ]}
            value={state.registrationType}
            onChange={(value) => {
              setState({
                ...state,
                registrationType: value as 'player' | 'team',
              })
            }}
            disabled={false}
            name='registrationType'
          />
        </div>
      </div> */}
      {/* separator */}
      <div
        css={css`
          width: 100%;
          height: 1px;
          background: ${colors.NEUTRAL_100};
          border-bottom: 1px solid ${colors.NEUTRAL_100};
        `}
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionSmall' weight='semiBold'>
          Other Settings
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Auto-Approve Registrations'
          subtitle='Registrations Will Be Automatically Approved'
          value={state.autoApproveRegistrations}
          onChange={(value) => {
            setState({ ...state, autoApproveRegistrations: value })
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <MultipleOptionsInput
          label='BCC Registration Emails:'
          tip='(Comma Separated, Press Enter To Add)'
          value={state.bccEmails}
          onChange={(value) => {
            setState({ ...state, bccEmails: value })
          }}
          placeholder='Enter email addresses'
          type='email'
          validator={emailRegex}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          name='bccEmails'
        />
      </div>
    </div>
  )
}

export default GeneralSettings
